import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { Link } from 'react-router-dom';
import MyCLALogo from '../../assets/MyCLALogo';
import './NavigationMenu.css';

const NAVIGATION_COLORS = {
  appBarBackground: '#fff',
  appBarText: '#000',
  menuItemText: '#8e918f',
};

export interface MenuOption {
  label: string;
  path: string;
  activePaths?: string[];
  requiredAuth?: boolean;
  icon?: React.ReactNode;
}

export interface NavigationMenuProps {
  menuOptions: MenuOption[];
  className?: string;
}

export function NavigationMenu({ menuOptions }: NavigationMenuProps) {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const collapseAt = menuOptions.length <= 3 ? 'sm' : 'lg';

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: NAVIGATION_COLORS.appBarBackground,
        color: NAVIGATION_COLORS.appBarText,
        pt: '30px',
        pl: '30px',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box className="navigation-logo-box">
            <MyCLALogo className="cla-logo" />
          </Box>

          <Box className="navigation-menu-box">
            <Box sx={{ display: { xs: 'flex', [collapseAt]: 'none' } }}>
              <IconButton
                size="large"
                aria-label="navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="primary"
              >
                <MenuIcon />
              </IconButton>
            </Box>

            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: 'block', lg: 'none' } }}
            >
              {menuOptions.map((option) => (
                <MenuItem
                  component={Link}
                  key={option.path || option.label}
                  to={option.path}
                  onClick={handleCloseNavMenu}
                  sx={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <Typography sx={{ textAlign: 'center' }}>{option.label}</Typography>
                </MenuItem>
              ))}
            </Menu>

            <Box
              sx={{
                display: { xs: 'none', [collapseAt]: 'flex' },
                ml: 1,
                pt: 3,
                minWidth: 0,
                justifyContent: 'flex-start',
                gap: { lg: 1, xl: 4 },
                flexWrap: 'nowrap',
              }}
            >
              {menuOptions.map((option) => (
                <Link
                  key={option.path || option.label}
                  to={option.path}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    flex: '0 0 auto',
                  }}
                >
                  <Button
                    sx={{
                      'pb': 2,
                      'color': NAVIGATION_COLORS.menuItemText,
                      'display': 'block',
                      'fontSize': 18,
                      '&:hover': {
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: 0,
                          height: '2px',
                          backgroundColor: 'primary.main',
                        },
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: '2px',
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.3s',
                      },
                      'textTransform': 'capitalize',
                    }}
                  >
                    {option.label}
                  </Button>
                </Link>
              ))}
            </Box>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default NavigationMenu;
